/**
 * Client Portal Theme
 * Custom Chakra UI theme for the TaskEye client portal microsite
 */

import { extendTheme, type ThemeConfig } from '@chakra-ui/react';

// Theme configuration
const config: ThemeConfig = {
  initialColorMode: 'light',
  useSystemColorMode: false,
};

// Brand colors - Teal/Turquoise theme (based on logo #60C3AE)
const colors = {
  brand: {
    50: '#e8f7f4',
    100: '#c2e9e0',
    200: '#9adccc',
    300: '#72ceb8',
    400: '#4ac1a4',
    500: '#60C3AE', // Primary brand color (logo teal)
    600: '#4d9c8b',
    700: '#3a7568',
    800: '#274e45',
    900: '#142722',
  },
  // Additional semantic colors
  success: {
    50: '#e6f9f0',
    100: '#b3eed4',
    200: '#80e3b8',
    300: '#4dd89c',
    400: '#1acd80',
    500: '#00a86b', // Success green
    600: '#008556',
    700: '#006241',
    800: '#003f2c',
    900: '#001c17',
  },
  error: {
    50: '#ffe6e6',
    100: '#ffb3b3',
    200: '#ff8080',
    300: '#ff4d4d',
    400: '#ff1a1a',
    500: '#e60000', // Error red
    600: '#b30000',
    700: '#800000',
    800: '#4d0000',
    900: '#1a0000',
  },
  warning: {
    50: '#fff9e6',
    100: '#ffecb3',
    200: '#ffdf80',
    300: '#ffd24d',
    400: '#ffc51a',
    500: '#e6a800', // Warning orange
    600: '#b38600',
    700: '#806400',
    800: '#4d4200',
    900: '#1a2000',
  },
};

// Typography
const fonts = {
  heading: 'system-ui, sans-serif',
  body: 'system-ui, sans-serif',
  mono: "ui-monospace, 'Cascadia Code', 'Source Code Pro', Menlo, Consolas, 'DejaVu Sans Mono', monospace",
};

// Font sizes
const fontSizes = {
  xs: '0.75rem',   // 12px
  sm: '0.875rem',  // 14px
  md: '1rem',      // 16px
  lg: '1.125rem',  // 18px
  xl: '1.25rem',   // 20px
  '2xl': '1.5rem', // 24px
  '3xl': '1.875rem', // 30px
  '4xl': '2.25rem',  // 36px
  '5xl': '3rem',     // 48px
};

// Spacing
const space = {
  px: '1px',
  0: '0',
  1: '0.25rem',  // 4px
  2: '0.5rem',   // 8px
  3: '0.75rem',  // 12px
  4: '1rem',     // 16px
  5: '1.25rem',  // 20px
  6: '1.5rem',   // 24px
  8: '2rem',     // 32px
  10: '2.5rem',  // 40px
  12: '3rem',    // 48px
  16: '4rem',    // 64px
  20: '5rem',    // 80px
  24: '6rem',    // 96px
};

// Breakpoints
const breakpoints = {
  base: '0em',    // 0px
  sm: '30em',     // 480px
  md: '48em',     // 768px
  lg: '62em',     // 992px
  xl: '80em',     // 1280px
  '2xl': '96em',  // 1536px
};

// Component style overrides
const components = {
  // Button styles
  Button: {
    baseStyle: {
      fontWeight: '600',
      borderRadius: 'md',
      _focus: {
        boxShadow: 'outline',
      },
    },
    sizes: {
      sm: {
        fontSize: 'sm',
        px: 3,
        py: 2,
      },
      md: {
        fontSize: 'md',
        px: 4,
        py: 2.5,
      },
      lg: {
        fontSize: 'lg',
        px: 6,
        py: 3,
      },
    },
    variants: {
      solid: {
        bg: 'brand.500',
        color: 'white',
        _hover: {
          bg: 'brand.600',
          _disabled: {
            bg: 'brand.500',
          },
        },
        _active: {
          bg: 'brand.700',
        },
      },
      outline: {
        borderColor: 'brand.500',
        color: 'brand.500',
        _hover: {
          bg: 'brand.50',
        },
      },
      ghost: {
        color: 'brand.500',
        _hover: {
          bg: 'brand.50',
        },
      },
    },
    defaultProps: {
      colorScheme: 'brand',
    },
  },

  // Input styles
  Input: {
    baseStyle: {
      field: {
        borderRadius: 'md',
        _focus: {
          borderColor: 'brand.500',
          boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
        },
      },
    },
    sizes: {
      md: {
        field: {
          fontSize: 'md',
          px: 4,
          py: 2,
          height: '40px',
        },
      },
      lg: {
        field: {
          fontSize: 'lg',
          px: 4,
          py: 3,
          height: '48px',
        },
      },
    },
    defaultProps: {
      size: 'md',
      focusBorderColor: 'brand.500',
    },
  },

  // Textarea styles
  Textarea: {
    baseStyle: {
      borderRadius: 'md',
      _focus: {
        borderColor: 'brand.500',
        boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
      },
    },
    defaultProps: {
      focusBorderColor: 'brand.500',
    },
  },

  // Select styles
  Select: {
    baseStyle: {
      field: {
        borderRadius: 'md',
        _focus: {
          borderColor: 'brand.500',
          boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
        },
      },
    },
    defaultProps: {
      focusBorderColor: 'brand.500',
    },
  },

  // Card styles
  Card: {
    baseStyle: {
      container: {
        bg: 'bg-surface',
        borderRadius: 'lg',
        borderWidth: '1px',
        borderColor: 'border-default',
        boxShadow: 'sm',
        color: 'text-primary',
        _hover: {
          boxShadow: 'md',
          borderColor: 'border-hover',
        },
        transition: 'all 0.2s',
      },
    },
  },

  // Table styles
  Table: {
    baseStyle: {
      th: {
        fontWeight: '600',
        textTransform: 'none',
        letterSpacing: 'normal',
        fontSize: 'sm',
        color: 'white',
      },
      td: {
        fontSize: 'sm',
      },
      tr: {
        bg: 'bg-surface',
      },
    },
    variants: {
      simple: {
        th: {
          borderColor: 'gray.800',
          bg: 'brand.500',
           color: 'white',
        },
        td: {
          borderColor: 'gray.800',
        },
        tr: {
          bg: 'bg-surface',
        },
      },
    },
  },

  // Badge styles
  Badge: {
    baseStyle: {
      fontWeight: '600',
      borderRadius: 'full',
      px: 2,
      py: 0.5,
    },
  },

  // Modal styles
  Modal: {
    baseStyle: {
      dialog: {
        borderRadius: 'lg',
      },
    },
  },

  // Drawer styles
  Drawer: {
    baseStyle: {
      dialog: {
        bg: 'bg-surface',
      },
    },
  },

  // Tabs styles
  Tabs: {
    variants: {
      line: {
        tab: {
          fontWeight: '500',
          color: 'text-secondary',
          _selected: {
            color: 'brand.500',
            borderColor: 'brand.500',
          },
          _hover: {
            color: 'brand.600',
          },
        },
      },
      'soft-rounded': {
        tab: {
          fontWeight: '500',
          color: 'text-secondary',
          borderRadius: 'md',
          _selected: {
            color: 'brand.500',
            bg: 'interactive-brand-hover',
          },
          _hover: {
            bg: 'interactive-hover',
          },
        },
      },
    },
  },

  // Menu styles
  Menu: {
    baseStyle: {
      list: {
        bg: 'bg-surface',
        borderRadius: 'md',
        boxShadow: 'lg',
        borderWidth: '1px',
        borderColor: 'border-default',
        py: 2,
      },
      item: {
        bg: 'bg-surface',
        color: 'text-primary',
        fontSize: 'sm',
        _hover: {
          bg: 'interactive-brand-hover',
        },
        _focus: {
          bg: 'interactive-brand-hover',
        },
      },
    },
  },


  // Divider styles
  Divider: {
    baseStyle: {
      borderColor: 'border-default',
    },
  },

  // Spinner styles
  Spinner: {
    defaultProps: {
      color: 'brand.500',
    },
  },

  // Switch styles
  Switch: {
    baseStyle: {
      track: {
        bg: 'border-default',
        border: '1px solid',
        borderColor: 'border-muted',
        _checked: {
          bg: 'brand.500',
          borderColor: 'brand.500',
        },
        _disabled: {
          bg: 'bg-muted',
          borderColor: 'border-default',
          opacity: 0.6,
        },
      },
      thumb: {
        bg: 'white',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        _checked: {
          bg: 'white',
        },
        _disabled: {
          bg: 'text-muted',
        },
      },
    },
    sizes: {
      md: {
        track: {
          width: '36px',
          height: '16px',
        },
        thumb: {
          width: '16px',
          height: '16px',
          _checked: {
            transform: 'translateX(16px)',
          },
        },
      },
      lg: {
        track: {
          width: '44px',
          height: '20px',
        },
        thumb: {
          width: '20px',
          height: '20px',
          _checked: {
            transform: 'translateX(20px)',
          },
        },
      },
    },
    defaultProps: {
      size: 'md',
      colorScheme: 'brand',
    },
  },

  // Form Label styles
  FormLabel: {
    baseStyle: {
      fontWeight: '500',
      fontSize: 'sm',
      mb: 1.5,
    },
  },

  // Form Error styles
  FormError: {
    baseStyle: {
      text: {
        fontSize: 'sm',
        mt: 1,
      },
    },
  },
};

// Global styles
const styles = {
  global: (props: any) => ({
    'html, body': {
      fontSize: '16px',
      color: props.colorMode === 'dark' ? '#FBFEFD' : '#211F21',
      lineHeight: '1.6',
      transition: 'background-color 0.2s, color 0.2s',
    },
    body: {
      bg: props.colorMode === 'dark' ? '#211F21' : '#FBFEFD',
    },
    // Scrollbar styles
    '::-webkit-scrollbar': {
      width: '8px',
      height: '8px',
    },
    '::-webkit-scrollbar-track': {
      bg: props.colorMode === 'dark' ? 'gray.800' : 'gray.100',
    },
    '::-webkit-scrollbar-thumb': {
      bg: props.colorMode === 'dark' ? 'gray.600' : 'gray.400',
      borderRadius: '4px',
      _hover: {
        bg: props.colorMode === 'dark' ? 'gray.500' : 'gray.500',
      },
    },
  }),
};

// Semantic tokens for consistent theming
const semanticTokens = {
  colors: {
    // Backgrounds
    'bg-canvas': {
      default: '#FBFEFD', // Light background from logo
      _dark: '#211F21', // Dark charcoal from logo
    },
    'bg-surface': {
      default: '#FDFEFE', // Off-white from logo
      _dark: '#2a2728', // Slightly lighter than bg-canvas for contrast
    },
    'bg-surface-hover': {
      default: '#F7FAFC', // Hover state for interactive surfaces
      _dark: '#363335', // Lighter hover state in dark mode
    },
    'bg-muted': {
      default: '#EDF2F7', // Muted backgrounds
      _dark: '#1a1819', // Darker muted backgrounds
    },
    'bg-modified': {
      default: '#FEFCBF', // Modified/unsaved state
      _dark: '#4d4200',
    },

    // Borders
    'border-default': {
      default: '#E2E8F0', // Standard borders
      _dark: '#3d3a3b', // Subtle borders in dark mode
    },
    'border-muted': {
      default: '#CBD5E0', // Muted borders
      _dark: '#504d4e', // Lighter muted borders
    },
    'border-hover': {
      default: '#A0AEC0', // Hover state borders
      _dark: '#605d5e', // Hover borders
    },
    'border-modified': {
      default: '#ECC94B', // Modified state border
      _dark: '#b38600',
    },
    'border-brand-light': {
      default: '#9adccc', // Brand light border
      _dark: '#3a7568', // Darker brand border
    },

    // Text
    'text-primary': {
      default: '#211F21', // Primary text (dark charcoal from logo)
      _dark: '#FBFEFD', // Light text from logo in dark mode
    },
    'text-secondary': {
      default: '#4A5568', // Secondary text
      _dark: '#CBD5E0', // Lighter secondary text
    },
    'text-muted': {
      default: '#718096', // Muted text
      _dark: '#A0AEC0', // Lighter muted text
    },
    'text-tertiary': {
      default: '#A0AEC0', // Tertiary/icon text
      _dark: '#718096', // Icon text
    },
    'text-on-brand': {
      default: '#FFFFFF', // Text on brand backgrounds
      _dark: '#FFFFFF',
    },

    // Interactive
    'interactive-hover': {
      default: '#F7FAFC', // Generic hover background
      _dark: '#363335',
    },
    'interactive-brand-hover': {
      default: '#e8f7f4', // Brand tint hover (brand.50)
      _dark: '#274e45', // Darker brand hover
    },

    // Status colors
    'status-error': {
      default: '#F56565',
      _dark: '#FC8181',
    },
    'status-success': {
      default: '#48BB78',
      _dark: '#68D391',
    },
    'status-warning': {
      default: '#ECC94B',
      _dark: '#F6E05E',
    },
    'status-info': {
      default: '#4299E1',
      _dark: '#63B3ED',
    },
  },
};

// Custom shadows
const shadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  outline: '0 0 0 3px rgba(96, 195, 174, 0.6)', // Teal outline based on #60C3AE
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
};

// Radii
const radii = {
  none: '0',
  sm: '0.125rem',
  base: '0.25rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '0.75rem',
  '2xl': '1rem',
  '3xl': '1.5rem',
  full: '9999px',
};

// Create and export the theme
export const clientTheme = extendTheme({
  config,
  colors,
  fonts,
  fontSizes,
  space,
  breakpoints,
  components,
  styles,
  semanticTokens,
  shadows,
  radii,
});

// Export individual parts for reference
export { colors, fonts, fontSizes, space, breakpoints };
